import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { discoverAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Slider } from '../../components/ui/slider';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ReliabilityBadge, CommitmentBadge } from '../../components/ReliabilityBadge';
import { TopProfileBadge, ResponsiveBadge } from '../../components/TopProfileBadge';
import { useAuth } from '../../context/AuthContext';
import { useInviteTalentToShift } from '../../components/InviteTalentToShiftProvider';
import AddToInviteCartButton from '../../components/AddToInviteCartButton';
import MarketBadge from '../../components/MarketBadge';
import CertificationList from '../../components/CertificationList';
import CertificationBadges from '../../components/CertificationBadges';  // Iter-225a
import GgfBadge from '../../components/GgfBadge';
import DiscoveryScopeBanner from '../../components/DiscoveryScopeBanner';  // Iter-290b Sprint D (#8)
import { TalentCardMedia } from '../../components/discover/TalentCardMedia';  // Iter-294d
import FeaturedAiTalentRibbon from '../../components/FeaturedAiTalentRibbon';  // Iter-294e
import {
  ArrowLeft, Search, MapPin, Star, Award, Grid3X3, Rows3,
  ChevronLeft, ChevronRight, Loader2, User, Filter, X, SlidersHorizontal, Heart, Lock, Crown, Sparkles, Shield, Flame, Zap, Image as ImageIcon, Send, Plane, Ban
} from 'lucide-react';
import { toast } from 'sonner';

const TalentCard = ({ talent, onClick, view, isFav, onToggleFav, isAdmin, onInvite, highlightMarketIds, isGgfClient }) => {
  const isGrid = view === 'grid';
  const isIncomplete = talent.profile_complete === false;
  return (
    <Card
      className={`bg-white border-gray-200 hover:shadow-lg transition-all cursor-pointer group overflow-hidden ${
        isGrid ? 'w-full' : 'w-64 shrink-0 snap-start'
      } ${talent.is_highlighted ? 'highlight-card-discovery' : ''} ${talent.is_premium ? 'premium-card-discovery' : ''} ${isIncomplete ? 'ring-1 ring-amber-200/70' : ''}`}
      onClick={() => onClick(talent.user_id, talent)}
      data-testid="talent-card"
    >
      <div className={`relative ${talent.is_highlighted ? 'highlight-photo-ring' : ''}`}>
        <TalentCardMedia
          photoUrl={talent.profile_photo_url}
          displayName={talent.display_name}
          // AI cards use a 4:5 portrait for a more agency-catalog feel;
          // human/hybrid stay square so the headshot crop matches the
          // current rest of Discovery.
          aspect={talent.talent_type === 'ai' ? 'portrait' : 'square'}
          // Forward-compat slots — flip these on when the avatar /
          // intro-video / voice-assistant APIs ship. They render as
          // no-ops while the URLs are null.
          liveAvatarUrl={talent.live_avatar_url}
          videoUrl={talent.intro_video_url}
          assistantEnabled={talent.assistant_enabled}
          testId={`talent-card-media-${talent.user_id}`}
        >
          <button
            onClick={e => { e.stopPropagation(); onToggleFav?.(talent.user_id); }}
            className={`w-9 h-9 rounded-full flex items-center justify-center transition-all shadow-md ${isFav ? 'bg-[#FF2D95] text-white' : 'bg-white/85 text-gray-500 hover:text-[#FF2D95] backdrop-blur-md'}`}
            data-testid={`fav-toggle-${talent.user_id}`}
            aria-label={isFav ? 'Remove from favorites' : 'Save to favorites'}
          >
            <Heart className={`w-4 h-4 ${isFav ? 'fill-white' : ''}`} />
          </button>
        </TalentCardMedia>
        {talent.talent_type === 'ai' && (
          <span
            className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-md"
            data-testid={`ai-avatar-label-${talent.user_id}`}
          >
            <Sparkles className="w-3 h-3" /> AI Avatar · Virtual Talent
          </span>
        )}
        {talent.is_highlighted && (
          <span className="highlight-feature-label" data-testid={`highlighted-feature-label-${talent.user_id}`}>
            <Sparkles className="w-3 h-3" />
            Featured Talent
          </span>
        )}
        {talent.distance_text && (
          <div className="absolute bottom-2 right-2 bg-white/85 text-gray-900 text-[11px] px-2 py-0.5 rounded-full backdrop-blur-sm shadow-sm" data-testid="distance-badge">
            <MapPin className="w-3 h-3 inline mr-0.5" />{talent.distance_text}
          </div>
        )}
      </div>
      {/* Iter-294d — Dedicated achievement-badge row. Lives OUTSIDE the
          photo frame so badges never obscure the headshot. The heart
          stays on the photo (it's a primary action, not a label). */}
      {(talent.is_premium || talent.is_top_profile || (talent.is_hybrid_ready && talent.talent_type !== 'ai') || isIncomplete) && (
        <div className="flex flex-wrap items-center gap-1.5 px-3.5 pt-3" data-testid={`talent-badges-row-${talent.user_id}`}>
          {talent.is_premium && (
            <Badge className="premium-badge-gold bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 text-gray-900 border-0 shadow-[0_2px_12px_rgba(245,158,11,0.4)] text-[10px] font-bold px-2 py-0.5" data-testid="premium-badge">
              <Crown className="w-3 h-3 mr-0.5" />Premium
            </Badge>
          )}
          {talent.is_top_profile && (
            <TopProfileBadge size="xs" testIdSuffix={talent.user_id} />
          )}
          {talent.is_hybrid_ready && talent.talent_type !== 'ai' && (
            <Badge
              className="bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-gray-900 border-0 text-[10px] font-bold px-2 py-0.5"
              data-testid={`hybrid-ready-badge-${talent.user_id}`}
            >
              Hybrid Ready
            </Badge>
          )}
          {isIncomplete && (
            <Badge className="bg-amber-500 text-white border-0 shadow-sm text-[10px] font-semibold" data-testid="incomplete-profile-badge">
              Incomplete Profile
            </Badge>
          )}
        </div>
      )}
      <CardContent className="p-3.5">
        <div className="flex items-start justify-between mb-1.5">
          <div>
            <h3 className="font-semibold text-gray-900 text-sm leading-tight" data-testid="talent-name">{talent.display_name}</h3>
            {talent.age_range && <span className="text-[11px] text-gray-500">{talent.age_range}</span>}
          </div>
          {talent.avg_rating > 0 && (
            <div className="flex items-center gap-0.5 shrink-0" data-testid="talent-rating">
              <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400" />
              <span className="text-xs font-medium text-gray-700">{talent.avg_rating}</span>
              <span className="text-[10px] text-gray-500">({talent.rating_count})</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2 min-w-0">
          <MapPin className="w-3 h-3 shrink-0" />
          <span className="truncate min-w-0 flex-1" data-testid="talent-location">{talent.city}, {talent.state}</span>
        </div>
        {(talent.markets?.length > 0 || talent.primary_market) && (
          <div className="mb-2" data-testid="talent-market-badge-wrap">
            <MarketBadge
              markets={talent.markets || []}
              primaryMarket={talent.primary_market}
              highlightMarketIds={highlightMarketIds}
              testId={`market-badge-card-${talent.user_id || talent.id || 'unknown'}`}
            />
          </div>
        )}
        {/* Iter-411x — Compact role summary: primary role + up to 3
            extras + "+N more". Custom talent-added roles participate
            in the total. Full list on the Booking Page. Falls back to
            the legacy `role_name` badge only when `primary_role` is
            missing. */}
        {(talent.primary_role || talent.role_name) ? (
          <div className="flex flex-wrap items-center gap-1" data-testid="talent-card-roles">
            <Badge
              variant="outline"
              className="text-[10px] border-cyan-200 text-cyan-700 bg-cyan-50"
              data-testid="role-badge"
            >
              {talent.primary_role || talent.role_name}
            </Badge>
            {Array.isArray(talent.extra_roles) && talent.extra_roles.map((rn, i) => (
              <Badge
                key={i}
                variant="outline"
                className="text-[10px] border-slate-200 text-slate-600 bg-slate-50"
                data-testid={`extra-role-${i}`}
              >
                {rn}
              </Badge>
            ))}
            {typeof talent.extra_more === 'number' && talent.extra_more > 0 && (
              <span
                className="text-[10px] text-slate-500 ml-0.5"
                data-testid="extra-more-count"
              >
                +{talent.extra_more} more
              </span>
            )}
          </div>
        ) : null}

        {/* Iter-411x — Available for Travel indicator (only when talent
            has explicitly opted in). Keeps cards uncluttered by default. */}
        {talent.available_for_travel && (
          <div
            className="inline-flex items-center gap-1 mt-1 text-[10px] text-cyan-700"
            data-testid="talent-card-travel"
          >
            <Plane className="w-3 h-3" strokeWidth={2} /> Available for Travel
          </div>
        )}

        {/* Iter-411x — Tagline (from the same `bio` field shown on
            Booking Page). Truncated to keep card compact. */}
        {talent.tagline && (
          <p
            className="text-[11px] text-slate-500 italic mt-1 line-clamp-2 leading-snug"
            data-testid="talent-card-tagline"
          >
            &ldquo;{talent.tagline}&rdquo;
          </p>
        )}

        {talent.minimum_rate && talent.talent_type !== 'ai' && (
          <span className="text-[10px] text-gray-500 mt-1 block" data-testid="minimum-rate-display">Min ${talent.minimum_rate}/hr</span>
        )}
        {/* Iter-174 — AI pricing on cards (always render strip for AI talent). */}
        {talent.talent_type === 'ai' && (
          <div className="mt-2 px-2.5 py-1.5 rounded-md bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-200 flex items-center justify-between gap-2" data-testid="ai-pricing-strip">
            <div className="min-w-0">
              {talent.base_package_price && (
                <p className="text-[11px] text-violet-900">
                  Starting at <span className="font-bold">${Number(talent.base_package_price).toLocaleString()}</span>
                </p>
              )}
              {talent.monthly_retainer_price && (
                <p className="text-[10px] text-violet-700/80">
                  ${Number(talent.monthly_retainer_price).toLocaleString()}/mo retainer
                </p>
              )}
              {!talent.base_package_price && !talent.monthly_retainer_price && (
                <p className="text-[10px] text-violet-700/80">Custom pricing</p>
              )}
            </div>
            <Sparkles className="w-3.5 h-3.5 text-fuchsia-500 shrink-0" />
          </div>
        )}
        {/* Reliability & Commitment Badges - Max 2 badges */}
        <div className="mt-2 flex flex-wrap gap-1" data-testid="reliability-badges">
          {(() => {
            const badges = [];
            // Priority 1: Top Talent or Reliable badge
            if (talent.reliability_badge === 'top_talent') {
              badges.push(
                <ReliabilityBadge key="rel" badge="top_talent" size="xs" showScore={false} showTooltip={true} />
              );
            } else if (talent.reliability_badge === 'reliable') {
              badges.push(
                <ReliabilityBadge key="rel" badge="reliable" size="xs" showScore={false} showTooltip={true} />
              );
            } else if (talent.reliability_badge === 'new_talent' || !talent.reliability_badge) {
              badges.push(
                <Badge key="new" variant="outline" className="text-[10px] bg-gray-50 text-gray-600 border-gray-200">
                  <User className="w-3 h-3 mr-1 text-gray-400" />
                  New
                </Badge>
              );
            }
            // Priority 2: High Commitment (only if first badge was shown)
            if (badges.length < 2 && talent.commitment_badge === 'high_commitment') {
              badges.push(
                <CommitmentBadge key="commit" badge="high_commitment" completed={talent.multiday_completed} size="xs" />
              );
            }
            // Iter-168 Phase 3 — Responsive badge if response_rate >= 80%.
            if (badges.length < 2 && talent.is_responsive) {
              badges.push(
                <ResponsiveBadge key="resp" size="xs" testIdSuffix={talent.user_id} />
              );
            }
            return badges.slice(0, 2); // Max 2 badges
          })()}
          {/* Iter-252 — GGF Certified chip rendered in badge row (not over photo).
              Visibility enforced by context: admin / GGF client / own profile. */}
          {talent.ggf_certified && (
            <GgfBadge
              certified
              score={talent.ggf_test_score}
              date={talent.ggf_certified_at}
              source={talent.ggf_certified_source}
              size="xs"
              showScore={false}
              context={{ isAdmin: !!isAdmin, isGgfClient: !!isGgfClient }}
            />
          )}
        </div>
        {/* Iter-202 — verified certifications surfaced on discover cards
            (compact pills) so clients can spot certified bartenders /
            servers / drivers without opening the profile. Shows nothing
            when the talent has no certifications. */}
        {Array.isArray(talent.certifications) && talent.certifications.length > 0 && (
          <div className="mt-2" data-testid={`talent-card-certs-${talent.user_id}`}>
            <CertificationList
              certifications={talent.certifications}
              variant="compact"
              showDocLink={false}
              testIdPrefix={`talent-card-cert-${talent.user_id}`}
            />
          </div>
        )}
        {/* Iter-225a — assessment-based certification badges (score + date)
            surfaced directly on Discovery cards. Brand Ambassador Certified,
            Bartender Certified, etc. */}
        {Array.isArray(talent.training_certifications) && talent.training_certifications.length > 0 && (
          <div className="mt-2" data-testid={`talent-card-training-certs-${talent.user_id}`}>
            <CertificationBadges
              certifications={talent.training_certifications}
              variant="compact"
              maxVisible={3}
              testIdPrefix={`talent-card-training-cert-${talent.user_id}`}
            />
          </div>
        )}
        {/* Iter-186 — admin-only Invite to Gig CTA on each talent card. */}
        {isAdmin && talent.talent_type !== 'ai' && (
          <div className="mt-2 flex gap-1.5">
            <Button
              size="sm"
              variant="outline"
              className="flex-1 border-cyan-300 text-cyan-700 hover:bg-cyan-50 text-xs h-8"
              onClick={(e) => {
                e.stopPropagation();
                onInvite?.(talent.user_id, talent.display_name || talent.first_name);
              }}
              data-testid={`invite-to-gig-card-${talent.user_id}`}
            >
              <Send className="w-3 h-3 mr-1" /> Invite
            </Button>
            <AddToInviteCartButton
              talent={{
                user_id: talent.user_id,
                name: talent.display_name || talent.first_name,
                photo_url: talent.profile_photo_url,
                role: talent.role_name,
                city: talent.city,
                state: talent.state,
                markets: talent.markets || [],
                primary_market: talent.primary_market || null,
              }}
              variant="icon"
              testIdSuffix={`discover-${talent.user_id}`}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const TalentDiscovery = () => {
  const navigate = useNavigate();
  const carouselRef = useRef(null);
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  // Iter-252 — Detect if the current client account is Guardian Garage Floors
  // so the GGF badge stays visible to them (and stays hidden for all other
  // clients per product direction).
  const isGgfClient = (() => {
    const name = (user?.company_name || user?.name || '').toLowerCase();
    return /\b(guardian garage floors|ggf)\b/.test(name);
  })();
  const { open: openInviteToShift } = useInviteTalentToShift();

  const [talent, setTalent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  // Iter-290b Sprint D (#8) — subscription scope state. Null = unrestricted.
  const [discoveryScopeLock, setDiscoveryScopeLock] = useState(null);
  const [missingAddressBlock, setMissingAddressBlock] = useState(null);
  const [upgradeGate, setUpgradeGate] = useState(null);
  const [roles, setRoles] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  // Iter-296 P1 — Outside-click + Escape dismiss the filter drawer.
  // The drawer is an inline expanding panel (not a Sheet), so without
  // this guard users got "stuck" if the toggle button scrolled out of
  // view. We attach the listener only while the drawer is open.
  const filtersPanelRef = useRef(null);
  useEffect(() => {
    if (!showFilters) return undefined;
    const onPointer = (e) => {
      const panel = filtersPanelRef.current;
      if (!panel) return;
      // Ignore clicks on the toggle button itself — it manages its own state.
      if (e.target.closest('[data-testid="filter-toggle"]')) return;
      if (!panel.contains(e.target)) setShowFilters(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setShowFilters(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [showFilters]);
  // Iter-171 — Hybrid marketplace tab: 'human' (default) | 'ai' | 'hybrid_ready'
  const [marketTab, setMarketTab] = useState('human');

  // Search params
  const [searchCity, setSearchCity] = useState('');
  const [searchState, setSearchState] = useState('');
  const [searchZip, setSearchZip] = useState('');
  const [searchCountry, setSearchCountry] = useState('US');
  const [searchFirstName, setSearchFirstName] = useState('');

  // Filters
  const [filterRole, setFilterRole] = useState('');
  const [filterTier, setFilterTier] = useState('');
  const [filterPremium, setFilterPremium] = useState(false);
  const [filterMinRating, setFilterMinRating] = useState(0);
  const [filterMaxDistance, setFilterMaxDistance] = useState(100);
  const [sortBy, setSortBy] = useState('distance');
  
  // Advanced casting filters
  const [filterGender, setFilterGender] = useState('');
  const [filterMinAge, setFilterMinAge] = useState('');
  const [filterMaxAge, setFilterMaxAge] = useState('');
  const [filterEthnicity, setFilterEthnicity] = useState('');
  const [filterInfluencer, setFilterInfluencer] = useState(false);
  const [filterSocialMedia, setFilterSocialMedia] = useState(false);
  const [filterMinFollowers, setFilterMinFollowers] = useState('');
  const [filterVerified, setFilterVerified] = useState(false);
  const [favIds, setFavIds] = useState(new Set());
  
  // NEW Advanced filters (Pro/Enterprise only)
  // Appearance
  const [filterEyeColor, setFilterEyeColor] = useState('');
  const [filterHairColor, setFilterHairColor] = useState('');
  const [filterHairLength, setFilterHairLength] = useState('');
  const [filterWeightMin, setFilterWeightMin] = useState('');
  const [filterWeightMax, setFilterWeightMax] = useState('');
  const [filterDressSize, setFilterDressSize] = useState('');
  const [filterShirtSize, setFilterShirtSize] = useState('');
  const [filterShoeMin, setFilterShoeMin] = useState('');
  const [filterShoeMax, setFilterShoeMax] = useState('');
  const [filterHeightMin, setFilterHeightMin] = useState('');
  const [filterHeightMax, setFilterHeightMax] = useState('');
  // Language
  const [filterBilingual, setFilterBilingual] = useState(false);
  const [filterLanguage, setFilterLanguage] = useState('');
  // Tattoos
  const [filterNoTattoos, setFilterNoTattoos] = useState(false);
  const [filterNoVisibleTattoos, setFilterNoVisibleTattoos] = useState(false);
  // Experience
  const [filterMinCompletedGigs, setFilterMinCompletedGigs] = useState('');
  const [filterHasCertifications, setFilterHasCertifications] = useState(false);
  const [filterFavoritesOnly, setFilterFavoritesOnly] = useState(false);
  const [filterPreviouslyWorked, setFilterPreviouslyWorked] = useState(false);
  
  // NEW: Reliability & Certification Filters
  const [filterCertifiedOnly, setFilterCertifiedOnly] = useState(false);
  // Iter-225a/h — per-module certification chips. Slug list MUST match the
  // actual `training_modules.slug` values shipped by V073 + V175 + V176.
  // Display order: most-engaged roles first.
  const [filterCertModules, setFilterCertModules] = useState([]);
  const CERT_MODULE_OPTIONS = [
    { slug: 'brand-ambassador-essentials',    label: 'Brand Ambassador Certified' },
    { slug: 'bartender-essentials',           label: 'Bartender Certified' },
    { slug: 'server-essentials',              label: 'Server Certified' },
    { slug: 'team-lead-fundamentals',         label: 'Team Lead Certified' },
    { slug: 'team-captain-operations',        label: 'Team Captain Certified' },
    { slug: 'event-manager-fundamentals',     label: 'Event Manager Certified' },
    { slug: 'product-specialist-essentials',  label: 'Product Specialist Certified' },
    { slug: 'street-team-lead-essentials',    label: 'Street Team Lead Certified' },
    { slug: 'food-safety-fundamentals',       label: 'Food Safety Certified' },
    { slug: 'customer-service-excellence',    label: 'Customer Service Certified' },
    { slug: 'event-safety-setup',             label: 'Event Safety Certified' },
    { slug: 'professional-check-in',          label: 'Check-In Specialist' },
  ];
  const [filterTopTalentOnly, setFilterTopTalentOnly] = useState(false);
  const [filterHighCommitment, setFilterHighCommitment] = useState(false);
  const [filterTrending, setFilterTrending] = useState(false);
  const [filterReliableOnly, setFilterReliableOnly] = useState(false); // Off by default — enable when data supports it
  const [filterPhotosOnly, setFilterPhotosOnly] = useState(false);
  
  // Subscription status
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [hasAdvancedAccess, setHasAdvancedAccess] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  // Iter-191 — Markets filter
  const [marketsList, setMarketsList] = useState([]);
  const [filterMarketIds, setFilterMarketIds] = useState([]);  // array of UUIDs
  const [filterMarketMode, setFilterMarketMode] = useState('any'); // 'any' | 'strict'

  useEffect(() => {
    fetchRoles();
    // Iter-294e — the initial search is handled by the useEffect on
    // `marketTab` below; calling searchTalent() here too caused a race
    // where the marketTab-change fetch and the mount fetch could
    // resolve out of order, leaving the AI tab showing a stale human
    // result. One owner = one fetch path.
    fetchFavIds();
    fetchSubscriptionStatus();
    // Iter-191 — load markets for filter dropdown.
    api.get('/markets', { params: { limit: 200 } })
      .then(r => setMarketsList(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  // Iter-171 — re-search when the marketplace tab changes.
  useEffect(() => {
    searchTalent(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketTab]);

  // Iter-296 — Auto-fetch on quick-filter / market / sort / advanced-filter
  // changes. Previously the pills only mutated state and required the
  // "Search" button click to actually re-fetch — which made every quick
  // filter look broken. Debounced to 250ms so rapid pill toggles coalesce
  // into a single request.
  const isMountedRef = useRef(false);
  useEffect(() => {
    if (!isMountedRef.current) {
      // Skip the very first render — the marketTab effect above already
      // owns the initial fetch.
      isMountedRef.current = true;
      return undefined;
    }
    const t = setTimeout(() => { searchTalent(1); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterPremium, filterTrending, filterReliableOnly, filterPhotosOnly,
    filterTopTalentOnly, filterCertifiedOnly, filterMinRating,
    filterMaxDistance, sortBy, filterRole, filterTier,
    filterMarketIds.join(','), filterMarketMode,
    // Advanced (Pro/Enterprise) filters — also re-fetch when toggled
    filterGender, filterInfluencer, filterSocialMedia, filterVerified,
    filterFavoritesOnly, filterPreviouslyWorked, filterHasCertifications,
    filterBilingual, filterNoTattoos, filterNoVisibleTattoos,
  ]);

  const fetchSubscriptionStatus = async () => {
    try {
      const res = await api.get('/monetization/client-subscriptions/status');
      setSubscriptionStatus(res.data);
      const planId = res.data?.plan_id;
      setHasAdvancedAccess(planId === 'pro' || planId === 'enterprise');
    } catch (e) {
      // No subscription or error - default to no access
      setSubscriptionStatus(null);
      setHasAdvancedAccess(false);
    }
  };

  const handleLockedFilterClick = () => {
    setShowUpgradeDialog(true);
  };

  const fetchFavIds = async () => {
    try {
      const res = await api.get('/client/favorites');
      const ids = (res.data || []).map(f => f.talent_id);
      setFavIds(new Set(ids));
    } catch {}
  };

  const toggleFav = async (talentId) => {
    try {
      if (favIds.has(talentId)) {
        await api.delete(`/client/favorites/${talentId}`);
        setFavIds(prev => { const n = new Set(prev); n.delete(talentId); return n; });
        toast.success('Removed from favorites');
      } else {
        await api.post('/client/favorites', { talent_id: talentId });
        setFavIds(prev => new Set([...prev, talentId]));
        toast.success('Added to favorites');
      }
    } catch { toast.error('Failed to update favorite'); }
  };

  const fetchRoles = async () => {
    try {
      const res = await discoverAPI.getRoles();
      setRoles(res.data);
    } catch {}
  };

  const searchTalent = async (pageNum = 1) => {
    setLoading(true);
    try {
      const params = { page: pageNum, limit: 24, sort_by: sortBy };
      if (searchCity) params.city = searchCity;
      if (searchState && searchState !== 'all_states') params.state = searchState;
      if (searchZip) params.zip_code = searchZip;
      if (searchCountry) params.country = searchCountry;
      if (searchFirstName && searchFirstName.trim()) params.first_name = searchFirstName.trim();
      if (filterRole) params.role_id = filterRole;
      if (filterTier) params.tier_type = filterTier;
      if (filterPremium) params.premium_only = true;
      if (filterTrending) params.trending_only = true;
      if (filterReliableOnly) params.reliable_only = true;
      if (filterPhotosOnly) params.photos_only = true;
      // Iter-290b Sprint D (#7) — Top Performing pill was previously local-only;
      // wire it through as top_talent_only so the backend filter applies.
      if (filterTopTalentOnly) params.top_talent_only = true;
      // Iter-290b Sprint D (#7) — new Certified pill (assessment-based certs).
      if (filterCertifiedOnly) params.certified_only = true;
      if (filterMinRating > 0) params.min_rating = filterMinRating;
      if (filterMaxDistance < 100) params.max_distance = filterMaxDistance;

      // Iter-191 — Market filter
      if (filterMarketIds.length > 0) {
        params.market_ids = filterMarketIds.join(',');
        params.market_match = filterMarketMode;
      }
      
      // Advanced casting filters
      if (filterGender) params.gender = filterGender;
      if (filterMinAge) params.min_age = parseInt(filterMinAge);
      if (filterMaxAge) params.max_age = parseInt(filterMaxAge);
      if (filterEthnicity) params.ethnicity = filterEthnicity;
      if (filterInfluencer) params.is_influencer = true;
      if (filterSocialMedia) params.has_social_media = true;
      if (filterMinFollowers) params.min_followers = parseInt(filterMinFollowers);
      if (filterVerified) params.verified_only = true;
      
      // NEW Advanced filters (Pro/Enterprise only)
      if (hasAdvancedAccess) {
        // Appearance
        if (filterEyeColor && filterEyeColor !== 'any') params.eye_color = filterEyeColor;
        if (filterHairColor && filterHairColor !== 'any') params.hair_color = filterHairColor;
        if (filterHairLength && filterHairLength !== 'any') params.hair_length = filterHairLength;
        if (filterWeightMin) params.weight_min_lbs = parseInt(filterWeightMin);
        if (filterWeightMax) params.weight_max_lbs = parseInt(filterWeightMax);
        if (filterDressSize && filterDressSize !== 'any') params.dress_size = filterDressSize;
        if (filterShirtSize && filterShirtSize !== 'any') params.shirt_size = filterShirtSize;
        if (filterShoeMin) params.shoe_size_min = parseFloat(filterShoeMin);
        if (filterShoeMax) params.shoe_size_max = parseFloat(filterShoeMax);
        if (filterHeightMin && filterHeightMin !== 'any') params.height_min_inches = parseInt(filterHeightMin);
        if (filterHeightMax && filterHeightMax !== 'any') params.height_max_inches = parseInt(filterHeightMax);
        // Language
        if (filterBilingual) params.is_bilingual = true;
        if (filterLanguage && filterLanguage !== 'any') params.language = filterLanguage;
        // Tattoos
        if (filterNoTattoos) params.no_tattoos = true;
        if (filterNoVisibleTattoos) params.no_visible_tattoos = true;
        // Experience
        if (filterPremium) params.filter_premium_talent = true;
        if (filterMinCompletedGigs) params.min_completed_gigs = parseInt(filterMinCompletedGigs);
        if (filterHasCertifications) params.has_certifications = true;
        // Iter-225a — assessment-based certifications filter
        if (filterCertifiedOnly) params.certified_only = true;
        if (filterCertModules.length > 0) {
          params.cert_modules = filterCertModules.join(',');
        }
        if (filterFavoritesOnly) params.favorites_only = true;
        if (filterPreviouslyWorked) params.previously_worked = true;
      }

      const res = await discoverAPI.searchTalent({ ...params, talent_type: marketTab });
      setTalent(res.data.talent);
      setTotal(res.data.total);
      setHasMore(res.data.has_more);
      // Iter-290b Sprint D (#8) — surface subscription scope lock for clients
      setDiscoveryScopeLock(res.data.discovery_scope_locked || null);
      setPage(pageNum);
      // Iter-171 Behavioral Metrics — log impressions for the cards we just showed.
      try {
        const ids = (res.data.talent || []).slice(0, 50).map(t => t.user_id).filter(Boolean);
        if (ids.length) {
          api.post('/metrics/search-impressions', { talent_ids: ids, talent_type: marketTab }).catch(() => {});
        }
      } catch { /* ignore */ }
    } catch (e) {
      // Iter-290b Sprint D (#8) — surface missing-company-address as a
      // dedicated blocker instead of a generic toast.
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 &&
          (detail?.code === 'discovery_subscription_required' || detail?.title)) {
        // Iter-435 — free client: show the "Unlock Talent Discovery"
        // upgrade prompt instead of a generic error / any records.
        setUpgradeGate(
          (detail && typeof detail === 'object') ? detail : {
            title: 'Unlock Talent Discovery',
            message: 'Upgrade your iEvntür subscription to search the talent marketplace.',
            cta: 'View Subscription Plans',
            cta_href: '/client/settings/subscription',
          },
        );
        setTalent([]);
        setTotal(0);
        setDiscoveryScopeLock(null);
        setMissingAddressBlock(null);
      } else if (e.response?.status === 422 && detail?.code === 'missing_company_address') {
        setMissingAddressBlock(detail);
        setTalent([]);
        setTotal(0);
        setDiscoveryScopeLock(null);
      } else {
        setMissingAddressBlock(null);
        toast.error('Failed to search talent');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    searchTalent(1);
  };

  const viewProfile = async (talentId, talentObj) => {
    // Iter-174 — AI talent has its own detail page (with pricing).
    if (talentObj && talentObj.talent_type === 'ai') {
      navigate(`/client/ai-talent/${talentId}`);
      return;
    }
    // Incomplete profile → trigger activation instead of opening profile detail
    if (talentObj && talentObj.profile_complete === false) {
      try {
        const res = await api.post(`/discover/activate-talent/${talentId}`);
        const d = res.data?.delivery || {};
        const label = (ch) => {
          const v = d[ch];
          if (!v) return `${ch}: —`;
          if (v === 'sent' || v === 'sandbox' || v === 'success') return `✅ ${ch}`;
          if (v.startsWith && v.startsWith('skipped')) return `⚪ ${ch}: ${v.replace('skipped: ', '')}`;
          return `❌ ${ch}: ${String(v).replace(/^error:\s*/, '').slice(0, 60)}`;
        };
        const summary = `${label('in_app')}  •  ${label('email')}  •  ${label('sms')}`;
        const allOk = ['in_app', 'email', 'sms'].every(
          (c) => !d[c] || d[c] === 'sent' || d[c] === 'sandbox' || d[c] === 'success' ||
                 (typeof d[c] === 'string' && d[c].startsWith('skipped'))
        );
        const anyOk = ['in_app', 'email', 'sms'].some(
          (c) => d[c] === 'sent' || d[c] === 'sandbox' || d[c] === 'success'
        );
        if (allOk && anyOk) {
          toast.success(`Talent notified — ${summary}`, { duration: 5500 });
        } else if (anyOk) {
          toast.warning(`Partially sent — ${summary}`, { duration: 7000 });
        } else {
          toast.error(`Notification failed — ${summary}`, { duration: 8000 });
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to send activation request');
      }
      return;
    }
    navigate(`/client/discover/${talentId}`);
  };

  const scrollCarousel = (dir) => {
    if (!carouselRef.current) return;
    const scrollAmt = 280;
    carouselRef.current.scrollBy({ left: dir * scrollAmt, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setFilterRole(''); setFilterTier(''); setFilterPremium(false);
    setFilterTrending(false); setFilterReliableOnly(false);
    setFilterPhotosOnly(false);
    setFilterMinRating(0); setFilterMaxDistance(100); setSortBy('distance');
    // Clear advanced filters
    setFilterGender(''); setFilterMinAge(''); setFilterMaxAge('');
    setFilterEthnicity(''); setFilterInfluencer(false); setFilterSocialMedia(false);
    setFilterMinFollowers(''); setFilterVerified(false);
    // Clear NEW advanced filters
    setFilterEyeColor(''); setFilterHairColor(''); setFilterHairLength('');
    setFilterWeightMin(''); setFilterWeightMax(''); setFilterDressSize('');
    setFilterShirtSize(''); setFilterShoeMin(''); setFilterShoeMax('');
    setFilterHeightMin(''); setFilterHeightMax('');
    setFilterBilingual(false); setFilterLanguage('');
    setFilterNoTattoos(false); setFilterNoVisibleTattoos(false);
    setFilterMinCompletedGigs(''); setFilterHasCertifications(false);
    setFilterFavoritesOnly(false); setFilterPreviouslyWorked(false);
    // Iter-191 — Markets filter
    setFilterMarketIds([]); setFilterMarketMode('any');
  };

  const hasActiveFilters = filterRole || filterTier || filterPremium || filterTrending || filterReliableOnly || filterPhotosOnly || filterMinRating > 0 || 
    filterMaxDistance < 100 || filterGender || filterMinAge || filterMaxAge || filterEthnicity || 
    filterInfluencer || filterSocialMedia || filterMinFollowers || filterVerified ||
    // NEW advanced filters
    filterEyeColor || filterHairColor || filterHairLength || filterWeightMin || filterWeightMax ||
    filterDressSize || filterShirtSize || filterShoeMin || filterShoeMax ||
    filterHeightMin || filterHeightMax || filterBilingual || filterLanguage ||
    filterNoTattoos || filterNoVisibleTattoos || filterMinCompletedGigs ||
    filterHasCertifications || filterFavoritesOnly || filterPreviouslyWorked ||
    filterMarketIds.length > 0;

  const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="talent-discovery-page">
      {/* Iter-435 — free-client upgrade gate. Shown when the backend returns
          403 discovery_subscription_required. No records are rendered. */}
      {upgradeGate && (
        <Dialog open={true} onOpenChange={() => navigate('/client/dashboard')}>
          <DialogContent className="sm:max-w-md" data-testid="discovery-upgrade-modal">
            <DialogHeader>
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 shadow-lg">
                <Crown className="h-7 w-7 text-white" />
              </div>
              <DialogTitle className="text-center text-xl font-bold" data-testid="discovery-upgrade-title">
                {upgradeGate.title || 'Unlock Talent Discovery'}
              </DialogTitle>
              <DialogDescription className="text-center text-sm text-gray-600 pt-1" data-testid="discovery-upgrade-message">
                {upgradeGate.message || 'Upgrade your iEvntür subscription to search the talent marketplace.'}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2 space-y-2">
              <Button
                className="w-full bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-700 hover:to-cyan-700 text-white"
                onClick={() => navigate(upgradeGate.cta_href || '/client/settings/subscription')}
                data-testid="discovery-upgrade-cta"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                {upgradeGate.cta || 'View Subscription Plans'}
              </Button>
              <Button
                variant="ghost"
                className="w-full text-gray-500"
                onClick={() => navigate('/client/dashboard')}
                data-testid="discovery-upgrade-dismiss"
              >
                Maybe later
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {/* Header — sticky only on desktop. On mobile the tall multi-row header
          would cover talent cards after scroll, so we let it scroll away. */}
      <header className="bg-white border-b border-gray-200 shadow-sm sm:sticky sm:top-0 sm:z-10">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/client/dashboard')} data-testid="back-button">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <h1 className="text-lg font-semibold text-gray-900">Discover Talent</h1>
              {/* Iter-205 — Hide raw pool count from client-facing copy. We
                  expose curated/matching results, not the platform's total
                  talent database size. */}
              {total > 0 && (
                <Badge variant="outline" className="text-xs" data-testid="talent-discovery-match-badge">
                  Matching talent
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/client/blocked-talent')}
                className="text-slate-500 hover:text-rose-600"
                data-testid="view-blocked-talent-btn"
                title="Manage blocked talent"
              >
                <Ban className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Blocked</span>
              </Button>
              <Button
                variant={viewMode === 'carousel' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('carousel')}
                className={viewMode === 'carousel' ? 'bg-[#03DFFC] hover:bg-[#03DFFC]' : ''}
                data-testid="carousel-toggle"
              >
                <Rows3 className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className={viewMode === 'grid' ? 'bg-[#03DFFC] hover:bg-[#03DFFC]' : ''}
                data-testid="grid-toggle"
              >
                <Grid3X3 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="flex items-end gap-3">
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2">
              <div className="lg:col-span-1">
                <Label className="text-xs text-gray-500">First Name</Label>
                <Input
                  value={searchFirstName}
                  onChange={e => setSearchFirstName(e.target.value)}
                  placeholder="e.g. Alex"
                  className="h-9 text-sm"
                  data-testid="search-first-name"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">City</Label>
                <Input value={searchCity} onChange={e => setSearchCity(e.target.value)} placeholder="e.g. Atlanta" className="h-9 text-sm" data-testid="search-city" />
              </div>
              <div>
                <Label className="text-xs text-gray-500">State/Province</Label>
                <Select value={searchState} onValueChange={setSearchState}>
                  <SelectTrigger className="h-9 text-sm" data-testid="search-state"><SelectValue placeholder="State" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-60">
                    <SelectItem value="all_states">All States</SelectItem>
                    {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Zip/Postal Code</Label>
                <Input value={searchZip} onChange={e => setSearchZip(e.target.value)} placeholder="30301" className="h-9 text-sm" data-testid="search-zip" />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Country</Label>
                <Select value={searchCountry} onValueChange={setSearchCountry}>
                  <SelectTrigger className="h-9 text-sm" data-testid="search-country"><SelectValue placeholder="Country" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="US">United States</SelectItem>
                    <SelectItem value="CA">Canada</SelectItem>
                    <SelectItem value="UK">United Kingdom</SelectItem>
                    <SelectItem value="AU">Australia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2">
                <Button type="submit" className="bg-[#03DFFC] hover:bg-[#03DFFC] h-9" data-testid="search-btn">
                  <Search className="w-4 h-4 mr-1.5" />Search
                </Button>
                <Button type="button" variant="outline" size="sm" className="h-9" onClick={() => setShowFilters(!showFilters)} data-testid="filter-toggle">
                  <SlidersHorizontal className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </form>

          {/* Iter-290b Sprint D (#8) — subscription scope banner + missing-address blocker */}
          {missingAddressBlock && (
            <div
              className="mt-4 mb-3 flex flex-col sm:flex-row items-start sm:items-center gap-3 px-4 py-3 rounded-xl border border-rose-200 bg-rose-50 text-rose-900"
              data-testid="missing-address-block"
            >
              <div className="flex-1 text-sm">
                <strong>Complete your business address to start discovering talent.</strong>
                <div className="text-xs mt-1 text-rose-700">{missingAddressBlock.message}</div>
              </div>
              <button
                type="button"
                onClick={() => navigate(missingAddressBlock.settings_path || '/client/settings/profile')}
                className="inline-flex items-center px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold"
                data-testid="missing-address-cta"
              >
                Open Settings →
              </button>
            </div>
          )}
          <DiscoveryScopeBanner scopeLock={discoveryScopeLock} />

          {/* Quick Filter Pills — Prominent, above advanced filters */}
          <div className="flex flex-wrap gap-2 mt-3" data-testid="quick-filter-pills">
            <button
              onClick={() => setFilterPremium(!filterPremium)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                filterPremium
                  ? 'premium-badge-gold bg-gradient-to-r from-amber-500 to-yellow-400 text-amber-900 border-amber-400 shadow-[0_0_12px_rgba(245,158,11,0.4)] scale-[1.03]'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:bg-amber-50'
              }`}
              data-testid="quick-filter-premium"
            >
              <Crown className="w-3.5 h-3.5" />Premium Only
            </button>
            <button
              onClick={() => setFilterTrending(!filterTrending)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                filterTrending
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white border-cyan-400 shadow-md scale-[1.03]'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-cyan-300 hover:bg-cyan-50'
              }`}
              data-testid="quick-filter-trending"
            >
              <Zap className="w-3.5 h-3.5" />Trending
            </button>
            <button
              onClick={() => setFilterTopTalentOnly(!filterTopTalentOnly)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                filterTopTalentOnly
                  ? 'bg-gradient-to-r from-amber-500 to-yellow-400 text-amber-900 border-amber-400 shadow-md scale-[1.03]'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:bg-amber-50'
              }`}
              data-testid="quick-filter-top-performer"
            >
              <Star className="w-3.5 h-3.5" />Top Performing
            </button>
            <button
              onClick={() => setFilterReliableOnly(!filterReliableOnly)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                filterReliableOnly
                  ? 'bg-emerald-500 text-white border-emerald-400 shadow-md scale-[1.03]'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-300 hover:bg-emerald-50'
              }`}
              data-testid="quick-filter-reliable"
            >
              <Shield className="w-3.5 h-3.5" />Reliable Only
            </button>
            <button
              onClick={() => setFilterPhotosOnly(!filterPhotosOnly)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                filterPhotosOnly
                  ? 'bg-indigo-500 text-white border-indigo-400 shadow-md scale-[1.03]'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'
              }`}
              data-testid="quick-filter-photos-only"
              aria-pressed={filterPhotosOnly}
            >
              <ImageIcon className="w-3.5 h-3.5" />Photos Only
            </button>
            {/* Iter-290b Sprint D (#7) — new Certified quick-filter pill */}
            <button
              onClick={() => setFilterCertifiedOnly(!filterCertifiedOnly)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                filterCertifiedOnly
                  ? 'bg-violet-500 text-white border-violet-400 shadow-md scale-[1.03]'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-violet-300 hover:bg-violet-50'
              }`}
              data-testid="quick-filter-certified"
              aria-pressed={filterCertifiedOnly}
            >
              <Shield className="w-3.5 h-3.5" />Certified
            </button>
          </div>

          {/* Premium Discovery CTA — inline banner (routes client to their subscription page) */}
          <button
            type="button"
            onClick={() => navigate('/client/settings/subscription')}
            className="mt-3 p-3 premium-card-discovery rounded-lg flex items-center justify-between w-full text-left hover:ring-2 hover:ring-amber-300 transition cursor-pointer"
            data-testid="premium-discovery-cta"
          >
            <div className="flex items-center gap-2">
              <Crown className="w-4 h-4 text-amber-600" />
              <span className="text-xs text-gray-700">Want your talent here? <strong className="text-amber-700">Upgrade to Premium</strong> for priority placement.</span>
            </div>
            <span className="text-xs font-semibold text-amber-700">Upgrade →</span>
          </button>

          {/* Filters Panel */}
          {showFilters && (
            <div ref={filtersPanelRef} className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200" data-testid="filters-panel">
              <div className="flex items-center justify-between mb-3 gap-2">
                <span className="text-sm font-medium text-gray-700">Filters</span>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-gray-500" data-testid="filters-clear-all">Clear all</Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowFilters(false)}
                    className="text-xs h-7 px-2"
                    data-testid="filters-close-btn"
                  >
                    <X className="w-3 h-3 mr-1" />Close
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Role</Label>
                  <Select value={filterRole} onValueChange={setFilterRole}>
                    <SelectTrigger className="h-8 text-xs" data-testid="filter-role"><SelectValue placeholder="All roles" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-60">
                      <SelectItem value="all_roles">All Roles</SelectItem>
                      {roles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Category</Label>
                  <Select value={filterTier} onValueChange={setFilterTier}>
                    <SelectTrigger className="h-8 text-xs" data-testid="filter-tier"><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all_tiers">All Categories</SelectItem>
                      <SelectItem value="labor">Labor</SelectItem>
                      <SelectItem value="hospitality">Hospitality</SelectItem>
                      <SelectItem value="premium_activation">Premium Activation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Min Rating</Label>
                  <Select value={String(filterMinRating)} onValueChange={v => setFilterMinRating(Number(v))}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="0">Any</SelectItem>
                      <SelectItem value="3">3+ Stars</SelectItem>
                      <SelectItem value="4">4+ Stars</SelectItem>
                      <SelectItem value="4.5">4.5+ Stars</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Max Distance</Label>
                  <Select value={String(filterMaxDistance)} onValueChange={v => setFilterMaxDistance(Number(v))}>
                    <SelectTrigger className="h-8 text-xs" data-testid="filter-distance"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {searchCountry === 'US' ? (
                        <>
                          <SelectItem value="10">10 miles</SelectItem>
                          <SelectItem value="25">25 miles</SelectItem>
                          <SelectItem value="50">50 miles</SelectItem>
                          <SelectItem value="100">100 miles</SelectItem>
                          <SelectItem value="99999">Any distance</SelectItem>
                        </>
                      ) : (
                        <>
                          <SelectItem value="16">25 km</SelectItem>
                          <SelectItem value="31">50 km</SelectItem>
                          <SelectItem value="62">100 km</SelectItem>
                          <SelectItem value="100">160 km</SelectItem>
                          <SelectItem value="99999">Any distance</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Sort By</Label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="distance">Nearest</SelectItem>
                      <SelectItem value="rating">Top Rated</SelectItem>
                      <SelectItem value="name">Name A-Z</SelectItem>
                      <SelectItem value="followers">Most Followers</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Iter-191 — Markets filter */}
              <div className="mt-4 pt-3 border-t border-gray-200" data-testid="markets-filter-section">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Markets</span>
                    <Badge className="bg-cyan-50 text-cyan-700 border border-cyan-200 text-[10px]">
                      Metro region filter
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1 bg-white border rounded-md p-0.5" data-testid="market-mode-toggle">
                    <button
                      type="button"
                      onClick={() => setFilterMarketMode('any')}
                      className={`px-2 py-0.5 text-[11px] rounded ${filterMarketMode === 'any' ? 'bg-cyan-600 text-white' : 'text-gray-600'}`}
                      data-testid="market-mode-any"
                    >Any</button>
                    <button
                      type="button"
                      onClick={() => setFilterMarketMode('strict')}
                      className={`px-2 py-0.5 text-[11px] rounded ${filterMarketMode === 'strict' ? 'bg-cyan-600 text-white' : 'text-gray-600'}`}
                      data-testid="market-mode-strict"
                    >Strict (primary only)</button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {marketsList.length === 0 && (
                    <span className="text-xs text-gray-400">No markets configured yet</span>
                  )}
                  {marketsList.map((m) => {
                    const sel = filterMarketIds.includes(m.id);
                    return (
                      <button
                        type="button"
                        key={m.id}
                        onClick={() => setFilterMarketIds(cur => sel ? cur.filter(x => x !== m.id) : [...cur, m.id])}
                        className={`text-[11px] rounded-full px-2.5 py-1 border transition ${sel ? 'bg-cyan-600 text-white border-cyan-600' : 'bg-white text-gray-700 border-gray-200 hover:border-cyan-300'}`}
                        data-testid={`market-filter-chip-${m.slug}`}
                      >
                        {m.metro_name}
                      </button>
                    );
                  })}
                  {filterMarketIds.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setFilterMarketIds([])}
                      className="text-[11px] underline text-gray-500 ml-1"
                      data-testid="market-filter-clear"
                    >Clear</button>
                  )}
                </div>
              </div>
              
              {/* Advanced Casting Filters */}
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Advanced Casting Filters</span>
                    {!hasAdvancedAccess && (
                      <Badge 
                        variant="outline" 
                        className="text-xs bg-amber-50 text-amber-700 border-amber-200 cursor-pointer hover:bg-amber-100"
                        onClick={handleLockedFilterClick}
                        data-testid="upgrade-badge"
                      >
                        <Lock className="w-3 h-3 mr-1" />
                        Upgrade to unlock
                      </Badge>
                    )}
                  </div>
                  {hasAdvancedAccess && (
                    <Badge variant="outline" className="text-xs bg-cyan-50 text-cyan-700 border-cyan-200">
                      <Crown className="w-3 h-3 mr-1" />
                      {subscriptionStatus?.plan_name || 'Pro'} Access
                    </Badge>
                  )}
                </div>
                
                <div className={`space-y-4 ${!hasAdvancedAccess ? 'relative' : ''}`}>
                  {/* Lock overlay for non-Pro users */}
                  {!hasAdvancedAccess && (
                    <div 
                      className="absolute inset-0 bg-gray-100/60 backdrop-blur-[1px] z-10 rounded-md flex items-center justify-center cursor-pointer"
                      onClick={handleLockedFilterClick}
                      data-testid="advanced-filters-lock-overlay"
                    >
                      <div className="flex items-center gap-2 bg-white/90 px-4 py-2 rounded-lg shadow-sm border border-gray-200">
                        <Lock className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">Upgrade to Pro to unlock</span>
                      </div>
                    </div>
                  )}
                  
                  {/* APPEARANCE Section */}
                  <div className="bg-gray-50/50 rounded-lg p-3 border border-gray-100">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                      <User className="w-3 h-3" /> Appearance
                    </h4>
                    <div className="grid grid-cols-6 gap-2">
                      <div>
                        <Label className="text-[10px] text-gray-500">Gender</Label>
                        <Select value={filterGender} onValueChange={setFilterGender} disabled={!hasAdvancedAccess}>
                          <SelectTrigger className={`h-7 text-xs ${!hasAdvancedAccess ? 'opacity-50' : ''}`} data-testid="filter-gender"><SelectValue placeholder="Any" /></SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="non_binary">Non-Binary</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px] text-gray-500">Eye Color</Label>
                        <Select value={filterEyeColor} onValueChange={setFilterEyeColor} disabled={!hasAdvancedAccess}>
                          <SelectTrigger className={`h-7 text-xs ${!hasAdvancedAccess ? 'opacity-50' : ''}`} data-testid="filter-eye-color"><SelectValue placeholder="Any" /></SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="blue">Blue</SelectItem>
                            <SelectItem value="green">Green</SelectItem>
                            <SelectItem value="brown">Brown</SelectItem>
                            <SelectItem value="hazel">Hazel</SelectItem>
                            <SelectItem value="gray">Gray</SelectItem>
                            <SelectItem value="amber">Amber</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px] text-gray-500">Hair Color</Label>
                        <Select value={filterHairColor} onValueChange={setFilterHairColor} disabled={!hasAdvancedAccess}>
                          <SelectTrigger className={`h-7 text-xs ${!hasAdvancedAccess ? 'opacity-50' : ''}`} data-testid="filter-hair-color"><SelectValue placeholder="Any" /></SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="black">Black</SelectItem>
                            <SelectItem value="brown">Brown</SelectItem>
                            <SelectItem value="blonde">Blonde</SelectItem>
                            <SelectItem value="red">Red</SelectItem>
                            <SelectItem value="auburn">Auburn</SelectItem>
                            <SelectItem value="gray">Gray/White</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px] text-gray-500">Hair Length</Label>
                        <Select value={filterHairLength} onValueChange={setFilterHairLength} disabled={!hasAdvancedAccess}>
                          <SelectTrigger className={`h-7 text-xs ${!hasAdvancedAccess ? 'opacity-50' : ''}`} data-testid="filter-hair-length"><SelectValue placeholder="Any" /></SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="bald">Bald</SelectItem>
                            <SelectItem value="buzz">Buzz</SelectItem>
                            <SelectItem value="short">Short</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="long">Long</SelectItem>
                            <SelectItem value="very_long">Very Long</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px] text-gray-500">Ethnicity</Label>
                        <Select value={filterEthnicity} onValueChange={setFilterEthnicity} disabled={!hasAdvancedAccess}>
                          <SelectTrigger className={`h-7 text-xs ${!hasAdvancedAccess ? 'opacity-50' : ''}`} data-testid="filter-ethnicity"><SelectValue placeholder="Any" /></SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="asian">Asian</SelectItem>
                            <SelectItem value="black">Black</SelectItem>
                            <SelectItem value="hispanic">Hispanic/Latino</SelectItem>
                            <SelectItem value="white">White</SelectItem>
                            <SelectItem value="mixed">Mixed/Multi</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <Label className="text-[10px] text-gray-500">Age Min</Label>
                          <Input type="number" value={filterMinAge} onChange={e => hasAdvancedAccess && setFilterMinAge(e.target.value)} placeholder="18" className={`h-7 text-xs ${!hasAdvancedAccess ? 'opacity-50' : ''}`} disabled={!hasAdvancedAccess} data-testid="filter-min-age" />
                        </div>
                        <div className="flex-1">
                          <Label className="text-[10px] text-gray-500">Age Max</Label>
                          <Input type="number" value={filterMaxAge} onChange={e => hasAdvancedAccess && setFilterMaxAge(e.target.value)} placeholder="45" className={`h-7 text-xs ${!hasAdvancedAccess ? 'opacity-50' : ''}`} disabled={!hasAdvancedAccess} data-testid="filter-max-age" />
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-6 gap-2 mt-2">
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <Label className="text-[10px] text-gray-500">Height Min</Label>
                          <Select value={filterHeightMin} onValueChange={setFilterHeightMin} disabled={!hasAdvancedAccess}>
                            <SelectTrigger className={`h-7 text-xs ${!hasAdvancedAccess ? 'opacity-50' : ''}`} data-testid="filter-height-min"><SelectValue placeholder="Any" /></SelectTrigger>
                            <SelectContent className="bg-white">
                              <SelectItem value="any">Any</SelectItem>
                              <SelectItem value="60">5'0"</SelectItem>
                              <SelectItem value="63">5'3"</SelectItem>
                              <SelectItem value="66">5'6"</SelectItem>
                              <SelectItem value="69">5'9"</SelectItem>
                              <SelectItem value="72">6'0"</SelectItem>
                              <SelectItem value="75">6'3"</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex-1">
                          <Label className="text-[10px] text-gray-500">Height Max</Label>
                          <Select value={filterHeightMax} onValueChange={setFilterHeightMax} disabled={!hasAdvancedAccess}>
                            <SelectTrigger className={`h-7 text-xs ${!hasAdvancedAccess ? 'opacity-50' : ''}`} data-testid="filter-height-max"><SelectValue placeholder="Any" /></SelectTrigger>
                            <SelectContent className="bg-white">
                              <SelectItem value="any">Any</SelectItem>
                              <SelectItem value="63">5'3"</SelectItem>
                              <SelectItem value="66">5'6"</SelectItem>
                              <SelectItem value="69">5'9"</SelectItem>
                              <SelectItem value="72">6'0"</SelectItem>
                              <SelectItem value="75">6'3"</SelectItem>
                              <SelectItem value="78">6'6"</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <Label className="text-[10px] text-gray-500">Weight Min</Label>
                          <Input type="number" value={filterWeightMin} onChange={e => hasAdvancedAccess && setFilterWeightMin(e.target.value)} placeholder="lbs" className={`h-7 text-xs ${!hasAdvancedAccess ? 'opacity-50' : ''}`} disabled={!hasAdvancedAccess} data-testid="filter-weight-min" />
                        </div>
                        <div className="flex-1">
                          <Label className="text-[10px] text-gray-500">Weight Max</Label>
                          <Input type="number" value={filterWeightMax} onChange={e => hasAdvancedAccess && setFilterWeightMax(e.target.value)} placeholder="lbs" className={`h-7 text-xs ${!hasAdvancedAccess ? 'opacity-50' : ''}`} disabled={!hasAdvancedAccess} data-testid="filter-weight-max" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px] text-gray-500">Dress Size</Label>
                        <Select value={filterDressSize} onValueChange={setFilterDressSize} disabled={!hasAdvancedAccess}>
                          <SelectTrigger className={`h-7 text-xs ${!hasAdvancedAccess ? 'opacity-50' : ''}`} data-testid="filter-dress-size"><SelectValue placeholder="Any" /></SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="XS">XS (0-2)</SelectItem>
                            <SelectItem value="S">S (4-6)</SelectItem>
                            <SelectItem value="M">M (8-10)</SelectItem>
                            <SelectItem value="L">L (12-14)</SelectItem>
                            <SelectItem value="XL">XL (16-18)</SelectItem>
                            <SelectItem value="XXL">XXL (20+)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px] text-gray-500">Shirt Size</Label>
                        <Select value={filterShirtSize} onValueChange={setFilterShirtSize} disabled={!hasAdvancedAccess}>
                          <SelectTrigger className={`h-7 text-xs ${!hasAdvancedAccess ? 'opacity-50' : ''}`} data-testid="filter-shirt-size"><SelectValue placeholder="Any" /></SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="XS">XS</SelectItem>
                            <SelectItem value="S">S</SelectItem>
                            <SelectItem value="M">M</SelectItem>
                            <SelectItem value="L">L</SelectItem>
                            <SelectItem value="XL">XL</SelectItem>
                            <SelectItem value="XXL">XXL</SelectItem>
                            <SelectItem value="XXXL">XXXL</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <Label className="text-[10px] text-gray-500">Shoe Min</Label>
                          <Input type="number" step="0.5" value={filterShoeMin} onChange={e => hasAdvancedAccess && setFilterShoeMin(e.target.value)} placeholder="US" className={`h-7 text-xs ${!hasAdvancedAccess ? 'opacity-50' : ''}`} disabled={!hasAdvancedAccess} data-testid="filter-shoe-min" />
                        </div>
                        <div className="flex-1">
                          <Label className="text-[10px] text-gray-500">Shoe Max</Label>
                          <Input type="number" step="0.5" value={filterShoeMax} onChange={e => hasAdvancedAccess && setFilterShoeMax(e.target.value)} placeholder="US" className={`h-7 text-xs ${!hasAdvancedAccess ? 'opacity-50' : ''}`} disabled={!hasAdvancedAccess} data-testid="filter-shoe-max" />
                        </div>
                      </div>
                      <div className="flex items-end gap-2">
                        <label className={`flex items-center gap-1.5 text-xs ${!hasAdvancedAccess ? 'opacity-50' : 'cursor-pointer'}`}>
                          <input type="checkbox" checked={filterNoTattoos} onChange={e => hasAdvancedAccess && setFilterNoTattoos(e.target.checked)} disabled={!hasAdvancedAccess} className="rounded border-gray-300 h-3 w-3" data-testid="filter-no-tattoos" />
                          <span className="text-gray-600">No Tattoos</span>
                        </label>
                        <label className={`flex items-center gap-1.5 text-xs ${!hasAdvancedAccess ? 'opacity-50' : 'cursor-pointer'}`}>
                          <input type="checkbox" checked={filterNoVisibleTattoos} onChange={e => hasAdvancedAccess && setFilterNoVisibleTattoos(e.target.checked)} disabled={!hasAdvancedAccess} className="rounded border-gray-300 h-3 w-3" data-testid="filter-no-visible-tattoos" />
                          <span className="text-gray-600">No Visible</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {/* LANGUAGE Section */}
                  <div className="bg-gray-50/50 rounded-lg p-3 border border-gray-100">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Language
                    </h4>
                    <div className="grid grid-cols-6 gap-2">
                      <div className="col-span-2">
                        <Label className="text-[10px] text-gray-500">Language</Label>
                        <Select value={filterLanguage} onValueChange={setFilterLanguage} disabled={!hasAdvancedAccess}>
                          <SelectTrigger className={`h-7 text-xs ${!hasAdvancedAccess ? 'opacity-50' : ''}`} data-testid="filter-language"><SelectValue placeholder="Any language" /></SelectTrigger>
                          <SelectContent className="bg-white max-h-60">
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="English">English</SelectItem>
                            <SelectItem value="Spanish">Spanish</SelectItem>
                            <SelectItem value="French">French</SelectItem>
                            <SelectItem value="German">German</SelectItem>
                            <SelectItem value="Italian">Italian</SelectItem>
                            <SelectItem value="Portuguese">Portuguese</SelectItem>
                            <SelectItem value="Chinese">Chinese</SelectItem>
                            <SelectItem value="Japanese">Japanese</SelectItem>
                            <SelectItem value="Korean">Korean</SelectItem>
                            <SelectItem value="Arabic">Arabic</SelectItem>
                            <SelectItem value="Hindi">Hindi</SelectItem>
                            <SelectItem value="Russian">Russian</SelectItem>
                            <SelectItem value="Vietnamese">Vietnamese</SelectItem>
                            <SelectItem value="Tagalog">Tagalog</SelectItem>
                            <SelectItem value="ASL">ASL</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2 flex items-end">
                        <label className={`flex items-center gap-1.5 text-xs ${!hasAdvancedAccess ? 'opacity-50' : 'cursor-pointer'}`}>
                          <input type="checkbox" checked={filterBilingual} onChange={e => hasAdvancedAccess && setFilterBilingual(e.target.checked)} disabled={!hasAdvancedAccess} className="rounded border-gray-300 h-3 w-3" data-testid="filter-bilingual" />
                          <span className="text-gray-600">Bilingual / Multilingual Only</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {/* EXPERIENCE Section */}
                  <div className="bg-gray-50/50 rounded-lg p-3 border border-gray-100">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                      <Award className="w-3 h-3" /> Experience & Certifications
                    </h4>
                    <div className="grid grid-cols-6 gap-2">
                      <div>
                        <Label className="text-[10px] text-gray-500">Min Completed Gigs</Label>
                        <Input type="number" value={filterMinCompletedGigs} onChange={e => hasAdvancedAccess && setFilterMinCompletedGigs(e.target.value)} placeholder="0" className={`h-7 text-xs ${!hasAdvancedAccess ? 'opacity-50' : ''}`} disabled={!hasAdvancedAccess} data-testid="filter-min-gigs" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-gray-500">Min Followers</Label>
                        <Input type="number" value={filterMinFollowers} onChange={e => hasAdvancedAccess && setFilterMinFollowers(e.target.value)} placeholder="1000" className={`h-7 text-xs ${!hasAdvancedAccess ? 'opacity-50' : ''}`} disabled={!hasAdvancedAccess} data-testid="filter-min-followers" />
                      </div>
                      <div className="col-span-4 flex items-end gap-4">
                        <label className={`flex items-center gap-1.5 text-xs ${!hasAdvancedAccess ? 'opacity-50' : 'cursor-pointer'}`}>
                          <input type="checkbox" checked={filterHasCertifications} onChange={e => hasAdvancedAccess && setFilterHasCertifications(e.target.checked)} disabled={!hasAdvancedAccess} className="rounded border-gray-300 h-3 w-3" data-testid="filter-certifications" />
                          <span className="text-gray-600">Has Certifications</span>
                        </label>
                        <label className={`flex items-center gap-1.5 text-xs ${!hasAdvancedAccess ? 'opacity-50' : 'cursor-pointer'}`}>
                          <input type="checkbox" checked={filterPreviouslyWorked} onChange={e => hasAdvancedAccess && setFilterPreviouslyWorked(e.target.checked)} disabled={!hasAdvancedAccess} className="rounded border-gray-300 h-3 w-3" data-testid="filter-previously-worked" />
                          <span className="text-gray-600">Previously Worked With</span>
                        </label>
                        <label className={`flex items-center gap-1.5 text-xs ${!hasAdvancedAccess ? 'opacity-50' : 'cursor-pointer'}`}>
                          <input type="checkbox" checked={filterFavoritesOnly} onChange={e => hasAdvancedAccess && setFilterFavoritesOnly(e.target.checked)} disabled={!hasAdvancedAccess} className="rounded border-gray-300 h-3 w-3" data-testid="filter-favorites-only" />
                          <span className="text-gray-600">Favorites Only</span>
                        </label>
                        <label className={`flex items-center gap-1.5 text-xs ${!hasAdvancedAccess ? 'opacity-50' : 'cursor-pointer'}`}>
                          <input type="checkbox" checked={filterPremium} onChange={e => hasAdvancedAccess && setFilterPremium(e.target.checked)} disabled={!hasAdvancedAccess} className="rounded border-gray-300 h-3 w-3" data-testid="premium-filter" />
                          <span className="text-gray-600">Premium Only</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {/* RELIABILITY & QUALITY Section - NEW */}
                  <div className="bg-gradient-to-r from-green-50 to-cyan-50 rounded-lg p-3 border border-green-100">
                    <h4 className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                      <Shield className="w-3 h-3" /> Quality & Reliability Filters
                    </h4>
                    <div className="flex items-center gap-4 flex-wrap">
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={filterCertifiedOnly} 
                          onChange={e => setFilterCertifiedOnly(e.target.checked)} 
                          className="rounded border-green-300 h-3 w-3 text-green-600" 
                          data-testid="filter-certified-only" 
                        />
                        <span className="text-gray-700 flex items-center gap-1">
                          <Award className="w-3 h-3 text-green-600" />
                          Certified Only
                        </span>
                      </label>
                      {/* Iter-225a — Per-module certification chips. ANY semantics. */}
                      <div className="basis-full flex flex-wrap gap-1 mt-1" data-testid="filter-cert-modules">
                        {CERT_MODULE_OPTIONS.map((opt) => {
                          const checked = filterCertModules.includes(opt.slug);
                          return (
                            <button
                              key={opt.slug}
                              type="button"
                              onClick={() => {
                                setFilterCertModules((cur) => (
                                  cur.includes(opt.slug)
                                    ? cur.filter((s) => s !== opt.slug)
                                    : [...cur, opt.slug]
                                ));
                              }}
                              data-testid={`filter-cert-module-${opt.slug}`}
                              className={`text-[11px] px-2 py-0.5 rounded-full border transition ${
                                checked
                                  ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                  : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-300'
                              }`}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={filterTopTalentOnly} 
                          onChange={e => setFilterTopTalentOnly(e.target.checked)} 
                          className="rounded border-green-300 h-3 w-3 text-green-600" 
                          data-testid="filter-top-talent-only" 
                        />
                        <span className="text-gray-700 flex items-center gap-1">
                          <Star className="w-3 h-3 text-green-600" />
                          Top Talent Only
                        </span>
                      </label>
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={filterHighCommitment} 
                          onChange={e => setFilterHighCommitment(e.target.checked)} 
                          className="rounded border-orange-300 h-3 w-3 text-orange-600" 
                          data-testid="filter-high-commitment" 
                        />
                        <span className="text-gray-700 flex items-center gap-1">
                          <Flame className="w-3 h-3 text-orange-500" />
                          High Commitment
                        </span>
                      </label>
                    </div>
                    <p className="text-[10px] text-gray-500 mt-2">
                      Quality filters help you find reliable, proven talent with verified track records.
                    </p>
                  </div>
                  
                  {/* Search Button */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={filterVerified} onChange={e => setFilterVerified(e.target.checked)} className="rounded border-gray-300" data-testid="verified-filter" />
                        <span className="text-gray-700">ID Verified</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={filterInfluencer} onChange={e => setFilterInfluencer(e.target.checked)} className="rounded border-gray-300" data-testid="influencer-filter" />
                        <span className="text-gray-700">Influencers</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={filterSocialMedia} onChange={e => setFilterSocialMedia(e.target.checked)} className="rounded border-gray-300" data-testid="social-filter" />
                        <span className="text-gray-700">Has Social Media</span>
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      {hasActiveFilters && (
                        <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-gray-500">
                          Clear All Filters
                        </Button>
                      )}
                      <Button size="sm" onClick={() => searchTalent(1)} className="bg-[#03DFFC] hover:bg-[#03DFFC]/90 text-xs" data-testid="search-btn">
                        <Search className="w-3 h-3 mr-1" /> Search Talent
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Iter-171 — Hybrid Marketplace tab switcher */}
        <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="market-type-tabs">
          {[
            { key: 'human',         label: 'Human Talent',   testid: 'tab-market-human' },
            { key: 'ai',            label: 'AI Talent',       testid: 'tab-market-ai' },
            { key: 'hybrid_ready',  label: 'Hybrid Campaigns', testid: 'tab-market-hybrid' },
          ].map(t => (
            <Button
              key={t.key}
              size="sm"
              variant={marketTab === t.key ? 'default' : 'outline'}
              onClick={() => { setMarketTab(t.key); setPage(1); }}
              className={marketTab === t.key
                ? (t.key === 'ai'
                    ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:opacity-90 text-white'
                    : t.key === 'hybrid_ready'
                    ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:opacity-90 text-gray-900'
                    : 'bg-cyan-500 hover:bg-cyan-600 text-white')
                : ''}
              data-testid={t.testid}
            >
              {t.label}
            </Button>
          ))}
          <span className="text-xs text-gray-500 ml-2">
            {marketTab === 'human'  && 'Verified human ambassadors, models, and event staff.'}
            {marketTab === 'ai'     && 'Curated AI ambassadors and virtual creators for digital + hybrid campaigns.'}
            {marketTab === 'hybrid_ready' && 'Talent available for combined live + digital campaign packages.'}
          </span>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#03DFFC]" /></div>
        ) : talent.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-600 mb-2">
              {marketTab === 'ai'
                ? 'No AI talent yet — admins are curating new ambassadors.'
                : marketTab === 'hybrid_ready'
                ? 'No hybrid-ready talent yet — check back soon.'
                : 'No matches found'}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {marketTab === 'ai'
                ? 'Curated AI ambassadors and virtual creators will appear here as they go live.'
                : marketTab === 'hybrid_ready'
                ? 'Talent flagged for combined live + digital campaign packages will surface in this tab.'
                : (hasActiveFilters
                    ? 'Try removing a filter to see more talent.'
                    : 'Try adjusting your search criteria or expanding the distance radius.')}
            </p>
            {hasActiveFilters && marketTab === 'human' && (
              <Button variant="outline" onClick={clearFilters} data-testid="clear-filters-btn">Clear All Filters</Button>
            )}
          </div>
        ) : viewMode === 'carousel' ? (
          /* Carousel View */
          <>
            {marketTab === 'ai' && (
              <FeaturedAiTalentRibbon talent={talent} onOpen={viewProfile} />
            )}
            <div className="relative" data-testid="carousel-view">
            <Button
              variant="outline"
              size="sm"
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 shadow-md rounded-full w-9 h-9 p-0"
              onClick={() => scrollCarousel(-1)}
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div
              ref={carouselRef}
              className="flex gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory px-10 pb-2"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {talent.map(t => (
                <TalentCard key={t.user_id} talent={t} onClick={viewProfile} view="carousel" isFav={favIds.has(t.user_id)} onToggleFav={toggleFav} isAdmin={isAdmin} isGgfClient={isGgfClient} onInvite={openInviteToShift} highlightMarketIds={filterMarketIds} />
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 shadow-md rounded-full w-9 h-9 p-0"
              onClick={() => scrollCarousel(1)}
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
          </>
        ) : (
          /* Grid View */
          <div data-testid="grid-view">
            {marketTab === 'ai' && (
              <FeaturedAiTalentRibbon talent={talent} onOpen={viewProfile} />
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {talent.map(t => (
                <TalentCard key={t.user_id} talent={t} onClick={viewProfile} view="grid" isFav={favIds.has(t.user_id)} onToggleFav={toggleFav} isAdmin={isAdmin} isGgfClient={isGgfClient} onInvite={openInviteToShift} highlightMarketIds={filterMarketIds} />
              ))}
            </div>
            {hasMore && (
              <div className="flex justify-center mt-8">
                <Button variant="outline" onClick={() => searchTalent(page + 1)} data-testid="load-more-btn">Load More</Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Upgrade Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="upgrade-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Crown className="w-5 h-5 text-amber-500" />
              Upgrade to Pro
            </DialogTitle>
            <DialogDescription>
              Unlock advanced talent filtering to find the perfect candidates faster.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-gradient-to-r from-cyan-50 to-purple-50 rounded-lg p-4 border border-cyan-100">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-cyan-500" />
                Pro Plan Benefits
              </h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs">✓</span>
                  Filter by physical attributes (age, gender, ethnicity)
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs">✓</span>
                  Filter by premium talent & past bookings
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs">✓</span>
                  Access your favorites filter
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs">✓</span>
                  5 free boosts per month
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs">✓</span>
                  20% off all boost purchases
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs">✓</span>
                  Priority email support
                </li>
              </ul>
            </div>
            <div className="text-center">
              <span className="text-3xl font-bold text-gray-900">$99</span>
              <span className="text-gray-500">/month</span>
            </div>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowUpgradeDialog(false)}
              className="flex-1"
            >
              Maybe Later
            </Button>
            <Button 
              onClick={() => navigate('/client/settings/subscription')}
              className="flex-1 bg-[#03DFFC] hover:bg-[#03DFFC]/90"
              data-testid="upgrade-now-btn"
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade Now
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TalentDiscovery;
